import { TrendingUp, AlertTriangle } from "lucide-react";
import { money } from "@/lib/format";
import { useI18n } from "@/context/I18nContext";

const DOT = { green: "bg-emerald-500", yellow: "bg-amber-400", red: "bg-rose-500" };
const TXT = { green: "text-emerald-700", yellow: "text-amber-600", red: "text-rose-600" };
const HEALTH_KEY = { green: "margin.hgood", yellow: "margin.hmed", red: "margin.hlow" };

function Row({ label, hint, value, cur, strong, positive, negative }) {
  const cls = negative ? "text-rose-600" : positive ? "text-emerald-700" : "text-slate-800";
  return (
    <div className="flex items-start justify-between gap-3 text-sm" data-testid={`margin-row-${label}`}>
      <span className="text-slate-500">{label}{hint && <span className="text-slate-400 text-xs"> ({hint})</span>}</span>
      <span className={`font-semibold shrink-0 ${strong ? "text-base" : ""} ${cls}`}>{value != null ? money(value, cur) : "—"}</span>
    </div>
  );
}

// Admin-only single source of truth for order profitability.
export function MarginPanel({ margin }) {
  const { t } = useI18n();
  if (!margin) return null;
  const m = margin;
  const cur = m.currency || "EUR";
  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 space-y-3 h-fit" data-testid="margin-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display text-lg font-semibold flex items-center gap-2"><TrendingUp size={18} className="text-emerald-600" /> {t("margin.title")}</h3>
        <span className="flex items-center gap-1.5" data-testid="margin-health">
          <span className={`w-2.5 h-2.5 rounded-full ${DOT[m.health]}`} />
          <span className={`text-xs font-semibold ${TXT[m.health]}`}>{t(HEALTH_KEY[m.health])}</span>
        </span>
      </div>
      <p className="text-xs text-slate-400 -mt-1">{t("margin.sub")}</p>

      <div className="space-y-2 pt-1">
        <Row label={t("margin.clientPrice")} hint={t("margin.h.clientPrice")} value={m.client_price} cur={cur} />
        <Row label={t("margin.partnerCost")} hint={t("margin.h.partnerCost")} value={m.partner_cost} cur={cur} negative />
        <div className="border-t border-slate-100 pt-2">
          <Row label={t("margin.gross")} hint={t("margin.h.gross")} value={m.gross_margin} cur={cur} positive />
        </div>
        <Row label={`${t("margin.stripe")} ${m.stripe_estimated ? t("margin.estimated") : ""}`.trim()} hint={t("margin.h.stripe")} value={-m.stripe_fees} cur={cur} negative />
        <Row label={t("margin.extra")} hint={t("margin.h.extra")} value={m.extra_charges} cur={cur} positive />
        <Row label={t("margin.penalties")} hint={t("margin.h.penalties")} value={m.partner_penalties} cur={cur} positive />
        {m.refunded > 0 && <Row label={t("margin.refunded")} hint={t("margin.h.refunded")} value={-m.refunded} cur={cur} negative />}
        {m.discounts > 0 && <Row label={t("margin.discounts")} hint={t("margin.h.discounts")} value={-m.discounts} cur={cur} negative />}
        {m.taxes > 0 && <Row label={t("margin.taxes")} hint={t("margin.h.taxes")} value={m.taxes} cur={cur} />}
      </div>

      <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
        <span className="font-semibold text-slate-700">{t("margin.net")}</span>
        <div className="text-right">
          <div className={`font-bold text-lg ${TXT[m.health]}`} data-testid="margin-net">{money(m.net_margin, cur)}</div>
          <div className={`text-xs font-semibold ${TXT[m.health]}`} data-testid="margin-net-pct">{m.net_margin_pct}% · {t("margin.min")} {m.min_margin_pct}%</div>
        </div>
      </div>

      {m.below_minimum && (
        <div className="flex items-start gap-2 rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-sm text-rose-700" data-testid="margin-below-min">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{t("margin.belowMin")}</span>
        </div>
      )}
    </div>
  );
}
